import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Autocomplete,
  Box,
  IconButton,
  InputAdornment,
  Popover,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Edit, Email, Info, Phone } from "@mui/icons-material";
import Customer from "../models/Customer";
import CustomerContact, {
  CustomerContactRole,
} from "../models/CustomerContact";
import { AppContext } from "../AppContextProvider";
import CustomerContactDialog from "./customers/CustomerContactDialog";
import { createCustomerContact } from "../api/ProcurementClient";
import ReadOnlyTextField from "./rfqs/ReadOnlyTextField";
import { copyTextToClipboard } from "../utils/copyTextToClipboard";

export interface BuyerAutocompleteRef {
  focus: () => void;
}

export interface BuyerAutocompleteProps {
  customer?: Customer;
  buyerName?: string;
  selectedBuyer?: CustomerContact;
  onBuyerSelected: (
    buyer: CustomerContact | undefined,
    isNew: boolean,
    confirmed: boolean
  ) => void;
  onBlur?: () => void;
  label?: string;
  required?: boolean;
  readOnly?: boolean;
  showCopyButton?: boolean;
  disabled?: boolean;
  sx?: object;
}

const emptyBuyer = (customerId: number): CustomerContact => ({
  id: 0,
  role: CustomerContactRole.BUYER,
  name: "",
  email: "",
  phone: "",
  altPhone: "",
  altEmail: "",
  customerId,
});

/** Normalized name for comparisons, buttons, parent sync — not for Autocomplete labels. */
const getBuyerDisplayName = (buyer: CustomerContact): string => {
  return buyer.name?.trim() ?? "";
};

const BuyerAutocomplete = forwardRef<
  BuyerAutocompleteRef,
  BuyerAutocompleteProps
>(
  (
    {
      customer,
      buyerName,
      selectedBuyer,
      onBuyerSelected,
      onBlur,
      label = "Buyer Name",
      required = true,
      readOnly = false,
      showCopyButton = false,
      disabled = false,
      sx = {},
    },
    ref
  ) => {
    const { customers, setCustomers } = useContext(AppContext);
    const textFieldRef = useRef<HTMLInputElement>(null);
    const buildEmptyBuyerWithName = (
      customerId: number,
      name: string | undefined
    ): CustomerContact => ({
      ...emptyBuyer(customerId),
      name: name ?? "",
    });

    // Always keep a controlled value for the Autocomplete. If `customer` is undefined,
    // we ignore `selectedBuyer` entirely and derive the value from `buyerName`.
    const [currentlySelectedBuyer, setCurrentlySelectedBuyer] =
      useState<CustomerContact>(() => {
        if (!customer) {
          return buildEmptyBuyerWithName(0, buyerName);
        }
        if (selectedBuyer) {
          return selectedBuyer;
        }
        return buildEmptyBuyerWithName(customer.id, buyerName);
      });
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showCreateFromPopoverDialog, setShowCreateFromPopoverDialog] =
      useState(false);
    const [contactPopoverAnchor, setContactPopoverAnchor] =
      useState<HTMLElement | null>(null);

    const isDisabled = disabled || !customer?.id;

    const empty = useMemo(
      () => emptyBuyer(customer?.id ?? 0),
      [customer?.id]
    );

    useEffect(() => {
      if (!customer) {
        setCurrentlySelectedBuyer(buildEmptyBuyerWithName(0, buyerName));
        return;
      }

      if (selectedBuyer) {
        const fromOptions = customer.contacts?.find(
          (c) => c.id === selectedBuyer.id
        );
        setCurrentlySelectedBuyer(fromOptions ?? selectedBuyer);
        return;
      }

      setCurrentlySelectedBuyer(buildEmptyBuyerWithName(customer.id, buyerName));
    }, [customer, selectedBuyer, buyerName]);

    useImperativeHandle(ref, () => ({
      focus: () => textFieldRef.current?.focus(),
    }));

    const sortedCustomerContacts: CustomerContact[] = useMemo(() => {
      if (!customer?.id) return [];
      return [...(customer.contacts ?? [])].sort((a, b) =>
        getBuyerDisplayName(a)
          .toLowerCase()
          .localeCompare(getBuyerDisplayName(b).toLowerCase())
      );
    }, [customer]);

    const handleBuyerChanged = (buyer: CustomerContact | undefined, isNew: boolean, confirmed: boolean) => {
      if (buyer === undefined) {
        // Keep Autocomplete controlled by always storing a concrete empty buyer object.
        setCurrentlySelectedBuyer(
          buildEmptyBuyerWithName(customer?.id ?? 0, "")
        );
      } else {
        setCurrentlySelectedBuyer(buyer);
      }

      // Notify parent for:
      // - actual selections (id > 0)
      // - cleared/empty input (undefined or empty name)
      // Don't notify parent while typing a partial "temp" buyer (id=0 with name).
      if (buyer?.id) {
        onBuyerSelected(buyer, isNew, confirmed);
      } else if (buyer === undefined || !getBuyerDisplayName(buyer)?.trim()) {
        onBuyerSelected(undefined, isNew, confirmed);
      }
    };

    const handleCreateBuyer = async (buyer: CustomerContact) => {
      if (!customer?.id) return;
      try {
        const createdBuyer = await createCustomerContact(customer.id, buyer);

        // Update app context customer contacts.
        const updatedCustomers = customers.map((c) =>
          c.id === customer.id
            ? {
                ...c,
                contacts: (c.contacts ?? []).concat(createdBuyer),
              }
            : c
        );
        setCustomers(updatedCustomers);

        setCurrentlySelectedBuyer(createdBuyer);
        onBuyerSelected(createdBuyer, true, true);
      } catch (e) {
        console.error("Failed to create customer contact:", e);
      }
    };

    const handleAddBuyerFromAdornmentClick = (
      event: React.MouseEvent<HTMLButtonElement>,
    ) => {
      event.preventDefault();
      setShowCreateFromPopoverDialog(true);
    };

    const handleShowContactInfo = (event: React.MouseEvent<HTMLElement>) => {
      setContactPopoverAnchor(event.currentTarget);
    };

    const handleCloseContactPopover = () => {
      setContactPopoverAnchor(null);
    };

    const handleEditBuyerClick = () => {
      setContactPopoverAnchor(null);
      setShowEditDialog(true);
    };

    const handleCreateBuyerClick = () => {
      setContactPopoverAnchor(null);
      setShowCreateFromPopoverDialog(true);
    };

    const handleEditBuyerDialogClose = (updatedBuyer?: CustomerContact) => {
      setShowEditDialog(false);
      if (!updatedBuyer?.id || !customer?.id) {
        return;
      }

      const updatedCustomers = customers.map((c) =>
        c.id === customer.id
          ? {
              ...c,
              contacts: (c.contacts ?? []).map((contact) =>
                contact.id === updatedBuyer.id ? updatedBuyer : contact
              ),
            }
          : c
      );
      setCustomers(updatedCustomers);
      setCurrentlySelectedBuyer(updatedBuyer);
      onBuyerSelected(updatedBuyer, false, true);
    };

    const handleCreateFromPopoverDialogClose = (
      createdBuyer?: CustomerContact
    ) => {
      setShowCreateFromPopoverDialog(false);
      if (createdBuyer) {
        void handleCreateBuyer(createdBuyer);
      }
    };

    const buyer = currentlySelectedBuyer;
    const showInfoButton = Boolean(
      customer &&
        buyer.id &&
        (buyer.email?.trim() || buyer.phone?.trim())
    );

    // Must return raw `name` (no trim). MUI Autocomplete resets the input from
    // getOptionLabel(value) whenever `value` reference changes; a new object is created
    // on every keystroke in freeSolo, so trimming here strips spaces (CustomerAutocomplete
    // uses raw option.name for the same reason).
    const getOptionLabel = (option: string | CustomerContact): string => {
      if (typeof option === "string") return option;
      return option.name ?? "";
    };

    const value = currentlySelectedBuyer;
    const inputValue = getOptionLabel(value);

    // Filter using our inputValue so the dropdown stays in sync on first keypress.
    const filterOptions = (options: CustomerContact[]) => {
      const query = inputValue.trim().toLowerCase();
      if (query === "") return options;
      return options.filter((opt) =>
        getBuyerDisplayName(opt).toLowerCase().includes(query)
      );
    };
    const handleTextFieldBlur = () => {
      onBlur?.();
    };

    const handleInputChange = (
      _: React.SyntheticEvent,
      newInputValue: string
    ) => {
      const trimmed = newInputValue.trim();

      // Only treat true empty input as cleared — not whitespace-only, so users can
      // type a leading space or pause on spaces while composing a name.
      if (newInputValue.length === 0) {
        handleBuyerChanged(undefined, false, false);
        return;
      }

      const existing = sortedCustomerContacts.find(
        (c) =>
          getBuyerDisplayName(c).toLowerCase() === trimmed.toLowerCase()
      );

      if (existing && newInputValue === trimmed) {
        handleBuyerChanged(existing, false, false);
        return;
      }

      handleBuyerChanged({
        ...empty,
        name: newInputValue,
      }, false, false);
    };

    if (readOnly) {
      return (
        <ReadOnlyTextField
          fullWidth
          inputRef={textFieldRef}
          label={label}
          value={buyer?.id ? getBuyerDisplayName(buyer) : ""}
          variant="standard"
          sx={{ flexGrow: 1, ...sx }}
          canCopy={showCopyButton}
        />
      );
    }

    return (
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ flexGrow: 1, ...sx }}
      >
        <Autocomplete
          freeSolo
          disableClearable
          options={sortedCustomerContacts}
          getOptionLabel={getOptionLabel}
          filterOptions={filterOptions}
          autoHighlight
          value={value}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          disabled={isDisabled}
          onChange={(_, newValue, reason) => {
            if (typeof newValue === "string") {
              const name = newValue.trim();
              if (!name.length) {
                handleBuyerChanged(undefined, false, false);
              } else {
                handleBuyerChanged({
                  ...empty,
                  name,
                }, false, false);
              }
              return;
            }

            if (newValue) {
              handleBuyerChanged(newValue, false, reason === "selectOption");
            } else {
              handleBuyerChanged(undefined, false, false);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              inputRef={textFieldRef}
              label={label}
              variant="standard"
              required={required}
              title={
                showCopyButton && inputValue.length > 0
                  ? "Double-click to copy"
                  : undefined
              }
              onDoubleClick={
                showCopyButton && inputValue.length > 0
                  ? (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      void copyTextToClipboard(inputValue);
                    }
                  : undefined
              }
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {params.InputProps?.endAdornment}
                      {showInfoButton ? (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={handleShowContactInfo}
                            aria-label="Show contact info"
                            tabIndex={-1}
                            sx={{ padding: 0 }}
                          >
                            <Info />
                          </IconButton>
                        </InputAdornment>
                      ) : null}
                      {customer?.id && !readOnly ? (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            color="primary"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={handleAddBuyerFromAdornmentClick}
                            aria-label="Add buyer"
                            sx={{ padding: 0 }}
                          >
                            <Add />
                          </IconButton>
                        </InputAdornment>
                      ) : null}
                    </>
                  ),
                },
              }}
            />
          )}
          sx={{ flexGrow: 1 }}
          onBlur={handleTextFieldBlur}
        />

        <Popover
          open={Boolean(contactPopoverAnchor)}
          anchorEl={contactPopoverAnchor}
          onClose={handleCloseContactPopover}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              gap: 1,
              p: 2,
              minWidth: 240,
              maxWidth: 360,
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ gridColumn: "span 2", mb: 0.5 }}
            >
              <Typography variant="subtitle2" fontWeight="bold">
                {buyer?.id ? getBuyerDisplayName(buyer) : "Contact"}
              </Typography>
              {customer ? (
                <Stack direction="row" spacing={0}>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={handleCreateBuyerClick}
                    aria-label="Create buyer"
                  >
                    <Add fontSize="small" />
                  </IconButton>
                  {buyer?.id ? (
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={handleEditBuyerClick}
                      aria-label="Edit buyer"
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  ) : null}
                </Stack>
              ) : null}
            </Stack>
            <Phone />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {buyer?.phone?.trim() || ""}
            </Typography>
            <Email />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {buyer?.email?.trim() || ""}
            </Typography>
          </Box>
        </Popover>

        {showEditDialog && customer && buyer?.id ? (
          <CustomerContactDialog
            customer={customer}
            contactRole={CustomerContactRole.BUYER}
            contact={buyer}
            onClose={handleEditBuyerDialogClose}
            saveOnClose={true}
          />
        ) : null}
        {showCreateFromPopoverDialog && customer ? (
          <CustomerContactDialog
            customer={customer}
            contactRole={CustomerContactRole.BUYER}
            onClose={handleCreateFromPopoverDialogClose}
            saveOnClose={true}
          />
        ) : null}
      </Stack>
    );
  }
);

BuyerAutocomplete.displayName = "BuyerAutocomplete";

export default BuyerAutocomplete;
